import React, { useState, useRef, useEffect } from 'react';
import { 
  Bold, Italic, Underline, Type, 
  Palette, Save, Eye, EyeOff 
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

const VisualEditor = ({ 
  previewComponent, 
  onSave, 
  globalSettings,
  title = "Visual Editor" 
}) => {
  const [selectedElement, setSelectedElement] = useState(null);
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0 });
  const [stylePanel, setStylePanel] = useState({
    color: '#000000',
    fontWeight: 'normal',
    fontStyle: 'normal',
    textDecoration: 'none',
    fontSize: '16px'
  });
  const [previewMode, setPreviewMode] = useState(false);
  const previewRef = useRef(null);

  // Handle click on element
  const handleElementClick = (e) => {
    if (previewMode) return;
    
    e.stopPropagation();
    const target = e.target;
    
    // Make element editable on double-click
    if (e.detail === 2) {
      target.contentEditable = true;
      target.focus();
      setSelectedElement(target);
    } else {
      // Single click - just select
      setSelectedElement(target);
      
      // Get current styles
      const computed = window.getComputedStyle(target);
      setStylePanel({
        color: computed.color,
        fontWeight: computed.fontWeight,
        fontStyle: computed.fontStyle,
        textDecoration: computed.textDecoration,
        fontSize: computed.fontSize
      });
    }
  };

  // Handle right-click context menu
  const handleContextMenu = (e) => {
    if (previewMode) return;
    
    e.preventDefault();
    setContextMenu({
      visible: true,
      x: e.pageX,
      y: e.pageY
    });
  };

  // Close context menu
  const closeContextMenu = () => {
    setContextMenu({ visible: false, x: 0, y: 0 });
  };

  // Apply style to selected element
  const applyStyle = (property, value) => {
    if (!selectedElement) return;
    
    selectedElement.style[property] = value;
    setStylePanel({ ...stylePanel, [property]: value });
    closeContextMenu();
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedElement) return;
      
      // Ctrl+B for Bold
      if (e.ctrlKey && e.key === 'b') {
        e.preventDefault();
        const newWeight = stylePanel.fontWeight === 'bold' ? 'normal' : 'bold';
        applyStyle('fontWeight', newWeight);
      }
      
      // Ctrl+I for Italic
      if (e.ctrlKey && e.key === 'i') {
        e.preventDefault();
        const newStyle = stylePanel.fontStyle === 'italic' ? 'normal' : 'italic';
        applyStyle('fontStyle', newStyle);
      }
      
      // Ctrl+U for Underline
      if (e.ctrlKey && e.key === 'u') {
        e.preventDefault();
        const newDecoration = stylePanel.textDecoration.includes('underline') ? 'none' : 'underline';
        applyStyle('textDecoration', newDecoration);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedElement, stylePanel]);

  // Click outside to deselect
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (previewRef.current && !previewRef.current.contains(e.target)) {
        if (selectedElement) {
          selectedElement.contentEditable = false;
        }
        setSelectedElement(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [selectedElement]);

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="bg-white border-b p-4 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold">{title}</h2>
          <Button
            size="sm"
            variant={previewMode ? "default" : "outline"}
            onClick={() => setPreviewMode(!previewMode)}
          >
            {previewMode ? <Eye className="h-4 w-4 mr-1" /> : <EyeOff className="h-4 w-4 mr-1" />}
            {previewMode ? 'Bearbeiten' : 'Vorschau'}
          </Button>
        </div>
        
        {/* Style Controls - Only show when element is selected */}
        {selectedElement && !previewMode && (
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => applyStyle('fontWeight', stylePanel.fontWeight === 'bold' ? 'normal' : 'bold')}
              className={stylePanel.fontWeight === 'bold' ? 'bg-blue-100' : ''}
            >
              <Bold className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => applyStyle('fontStyle', stylePanel.fontStyle === 'italic' ? 'normal' : 'italic')}
              className={stylePanel.fontStyle === 'italic' ? 'bg-blue-100' : ''}
            >
              <Italic className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => applyStyle('textDecoration', stylePanel.textDecoration.includes('underline') ? 'none' : 'underline')}
              className={stylePanel.textDecoration.includes('underline') ? 'bg-blue-100' : ''}
            >
              <Underline className="h-4 w-4" />
            </Button>
            
            <div className="flex items-center gap-1 ml-2">
              <Palette className="h-4 w-4 text-gray-600" />
              <input
                type="color"
                value={stylePanel.color}
                onChange={(e) => applyStyle('color', e.target.value)}
                className="w-8 h-8 cursor-pointer"
              />
            </div>
            
            <select
              value={stylePanel.fontSize}
              onChange={(e) => applyStyle('fontSize', e.target.value)}
              className="border rounded px-2 py-1 text-sm"
            >
              <option value="12px">12px</option>
              <option value="14px">14px</option>
              <option value="16px">16px</option>
              <option value="18px">18px</option>
              <option value="20px">20px</option>
              <option value="24px">24px</option>
              <option value="32px">32px</option>
            </select>
          </div>
        )}
        
        <Button onClick={onSave} className="ml-auto">
          <Save className="h-4 w-4 mr-2" />
          Speichern & Veröffentlichen
        </Button>
      </div>

      {/* Helper Text */}
      {!previewMode && (
        <div className="bg-blue-50 border-b border-blue-200 px-4 py-2 text-sm text-blue-800">
          💡 <strong>Tipp:</strong> Einmal klicken zum Auswählen • Doppelklick zum Text bearbeiten • Rechtsklick für mehr Optionen • Strg+B (Fett) • Strg+I (Kursiv) • Strg+U (Unterstrichen)
        </div>
      )}

      {/* Preview Container */}
      <div 
        ref={previewRef}
        className="flex-1 overflow-auto p-8 bg-gray-100"
        onClick={handleElementClick}
        onContextMenu={handleContextMenu}
        style={{ cursor: previewMode ? 'default' : 'pointer' }}
      >
        <div className={`max-w-4xl mx-auto ${selectedElement ? 'ring-2 ring-blue-400' : ''}`}>
          {previewComponent}
        </div>
      </div>

      {/* Context Menu */}
      {contextMenu.visible && (
        <div
          className="fixed bg-white border rounded-lg shadow-lg py-2 z-50"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          <button
            className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2"
            onClick={() => applyStyle('fontWeight', 'bold')}
          >
            <Bold className="h-4 w-4" /> Fett
          </button>
          <button
            className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2"
            onClick={() => applyStyle('fontStyle', 'italic')}
          >
            <Italic className="h-4 w-4" /> Kursiv
          </button>
          <button
            className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2"
            onClick={() => applyStyle('textDecoration', 'underline')}
          >
            <Underline className="h-4 w-4" /> Unterstrichen
          </button>
          <hr className="my-1" />
          <div className="px-4 py-2 flex items-center gap-2">
            <Palette className="h-4 w-4" />
            <input
              type="color"
              onChange={(e) => applyStyle('color', e.target.value)}
              className="cursor-pointer"
            />
            <span className="text-sm">Farbe</span>
          </div>
        </div>
      )}

      {/* Global Settings Section */}
      {globalSettings && (
        <div className="border-t bg-white p-6">
          <h3 className="text-lg font-semibold mb-4">Globale Einstellungen</h3>
          {globalSettings}
        </div>
      )}
    </div>
  );
};

export default VisualEditor;
